// ✅ src/components/DatingProfileCreationScreen.js (FULL)
// Updates (ONLY what you requested):
// ✅ Preferred age "FROM" is now selectable (18..80) (not locked on 18)
// ✅ Max age still required
// ✅ Validates min <= max (in-app popup)
// ✅ NEW: Profile picture (at least 1) is REQUIRED at save time (popup + jumps to Photo step)
// ❗Everything else kept the same

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/** ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}

function slugifyUsername(v) {
  return safe(v)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 20);
}

function calcAgeFromISODate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age < 0 ? null : age;
}

function uniq(arr) {
  return Array.from(new Set((arr || []).map((x) => safe(x)).filter(Boolean)));
}

function titleCase(s) {
  const v = safe(s);
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/** ---------------- main ---------------- */
export default function DatingProfileCreationScreen() {
  const nav = useNavigate();

  // auth
  const [email, setEmail] = useState("");

  // core
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [gender, setGender] = useState("female");
  const [lookingFor, setLookingFor] = useState("male");
  const [birthDate, setBirthDate] = useState(""); // YYYY-MM-DD
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("Ghana");
  const [bio, setBio] = useState("");

  // extras
  const [height, setHeight] = useState("");
  const [education, setEducation] = useState("none");
  const [job, setJob] = useState("none");
  const [jobOther, setJobOther] = useState("");

  // ✅ relationship goal (required at save time)
  const [relationshipGoal, setRelationshipGoal] = useState("");

  const [smoking, setSmoking] = useState("no");
  const [drinking, setDrinking] = useState("occasionally");
  const [religion, setReligion] = useState("none");

  // ✅ UPDATED: preferred age filter (both selectable now; max still required)
  const [prefAgeMin, setPrefAgeMin] = useState("18"); // selectable (stored as string for <select>)
  const [prefAgeMax, setPrefAgeMax] = useState(""); // required

  // tags
  const [languages, setLanguages] = useState(["English"]);
  const [interests, setInterests] = useState([]);

  // photos
  const [photos, setPhotos] = useState([]); // urls
  const [avatar, setAvatar] = useState("");

  // ui
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [step, setStep] = useState(0);

  // ✅ in-app popup state
  const [popOpen, setPopOpen] = useState(false);
  const [popText, setPopText] = useState("");

  const showPop = (text) => {
    setPopText(safe(text) || "Please fix this field.");
    setPopOpen(true);
  };
  const closePop = () => setPopOpen(false);

  const age = useMemo(() => calcAgeFromISODate(birthDate), [birthDate]);

  /** ---------------- OPTIONS ---------------- */
  const interestOptions = useMemo(
    () => [
      "music",
      "dancing",
      "movies",
      "football",
      "gym",
      "travel",
      "food",
      "cooking",
      "fashion",
      "gaming",
      "reading",
      "anime",
      "art",
      "photography",
      "beach",
      "hiking",
      "night outs",
      "church",
      "business",
      "tech",
      "comedy",
      "pets",
      "makeup",
      "netflix",
    ],
    []
  );

  const languageOptions = useMemo(
    () => ["English", "Twi", "Ga", "Ewe", "Hausa", "Fante", "French", "Other"],
    []
  );

  const educationOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "high_school", label: "High school" },
      { value: "college", label: "College" },
      { value: "university", label: "University" },
      { value: "masters", label: "Masters" },
      { value: "phd", label: "PhD" },
      { value: "vocational", label: "Vocational/Technical" },
    ],
    []
  );

  const jobOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "student", label: "Student" },
      { value: "self_employed", label: "Self-employed" },
      { value: "business_owner", label: "Business owner" },
      { value: "developer", label: "Software/IT" },
      { value: "healthcare", label: "Healthcare" },
      { value: "teacher", label: "Teacher" },
      { value: "finance", label: "Finance/Banking" },
      { value: "sales", label: "Sales/Marketing" },
      { value: "creative", label: "Creative/Media" },
      { value: "hospitality", label: "Hospitality" },
      { value: "public_service", label: "Public service" },
      { value: "trader", label: "Trader" },
      { value: "engineer", label: "Engineer" },
      { value: "other", label: "Other (type it)" },
    ],
    []
  );

  const relationshipGoalOptions = useMemo(
    () => [
      { value: "", label: "— Choose relationship goal —" },
      { value: "serious", label: "A serious relationship" },
      { value: "dating", label: "Dating" },
      { value: "friends", label: "Friends first" },
      { value: "meetups", label: "Meetups" },
      { value: "marriage", label: "Marriage minded" },
      { value: "not_sure", label: "Not sure yet" },
    ],
    []
  );

  // ✅ age options (18..80)
  const ageOptions = useMemo(() => {
    const out = [];
    for (let i = 18; i <= 80; i += 1) out.push(i);
    return out;
  }, []);

  const smokingOptions = useMemo(
    () => [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasionally" },
      { value: "yes", label: "Yes" },
    ],
    []
  );

  const drinkingOptions = useMemo(
    () => [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasionally" },
      { value: "socially", label: "Socially" },
      { value: "yes", label: "Yes" },
    ],
    []
  );

  const religionOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "christian", label: "Christian" },
      { value: "muslim", label: "Muslim" },
      { value: "traditional", label: "Traditional" },
      { value: "other", label: "Other" },
    ],
    []
  );

  const tabs = useMemo(
    () => [
      { key: "account", label: "Account Info" },
      { key: "details", label: "Profile Details" },
      { key: "interests", label: "Interests" },
      { key: "lifestyle", label: "Lifestyle" },
      { key: "photo", label: "Photo" },
    ],
    []
  );

  const activeTabIndex = useMemo(() => {
    if (step === 0) return 0;
    if (step === 1 || step === 2) return 1;
    if (step === 4) return 3;
    if (step === 3) return 4;
    return 0;
  }, [step]);

  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 900);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const styles = useMemo(
    () => ({
      page: {
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f7f9ff 0%, #eef3ff 40%, #f6f7fb 100%)",
        color: "#0f172a",
      },

      // ✅ Clean Top nav
      nav: {
        height: 56,
        background: "linear-gradient(180deg, #2f5fa8 0%, #2b5799 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 10px 30px rgba(13, 39, 89, 0.18)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      navInner: {
        width: "100%",
        maxWidth: 1100,
        padding: "0 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      },
      brand: {
        display: "flex",
        gap: 10,
        alignItems: "center",
        fontWeight: 950,
        letterSpacing: -0.2,
        cursor: "pointer",
        userSelect: "none",
      },
      brandDot: {
        width: 12,
        height: 12,
        borderRadius: 999,
        background: "linear-gradient(135deg, #ec4899, #3b82f6)",
        boxShadow: "0 10px 22px rgba(236,72,153,0.22)",
      },
      brandText: { fontSize: 14, opacity: 0.98 },
      navRight: { display: "flex", gap: 12, alignItems: "center" },
      navPill: {
        padding: "8px 10px",
        background: "rgba(255,255,255,0.14)",
        border: "1px solid rgba(255,255,255,0.22)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        maxWidth: 320,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },

      wrap: {
        width: "100%",
        maxWidth: 1100,
        margin: "0 auto",
        padding: isNarrow ? "18px 14px" : "28px 18px",
      },

      title: {
        textAlign: "center",
        fontSize: isNarrow ? 30 : 42,
        fontWeight: 950,
        color: "#1f2a44",
        letterSpacing: -0.6,
        marginTop: 10,
      },
      subtitle: {
        textAlign: "center",
        marginTop: 8,
        fontSize: 15,
        color: "#475569",
      },

      stepTabs: {
        marginTop: 18,
        background: "#ffffff",
        border: "1px solid #e6ebf5",
        borderRadius: 14,
        padding: "10px 12px",
        display: "flex",
        gap: 10,
        overflowX: "auto",
        boxShadow: "0 12px 26px rgba(15, 23, 42, 0.06)",
      },
      tab: (active) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 12,
        border: active
          ? "1px solid rgba(236, 72, 153, 0.25)"
          : "1px solid transparent",
        background: active
          ? "linear-gradient(180deg, rgba(236,72,153,0.10), rgba(59,130,246,0.08))"
          : "transparent",
        color: active ? "#1f2a44" : "#50607a",
        fontWeight: 950,
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
      }),
      tabNum: (active) => ({
        width: 26,
        height: 26,
        borderRadius: 999,
        display: "grid",
        placeItems: "center",
        fontWeight: 950,
        fontSize: 13,
        background: active
          ? "linear-gradient(135deg, #ec4899, #3b82f6)"
          : "#e9eefb",
        color: active ? "#fff" : "#2b3a55",
      }),

      grid: {
        marginTop: 18,
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
        gap: 18,
        alignItems: "start",
      },

      card: {
        background: "#ffffff",
        border: "1px solid #e6ebf5",
        borderRadius: 16,
        boxShadow: "0 12px 26px rgba(15, 23, 42, 0.06)",
      },

      leftCard: { padding: 16 },
      rightCard: { padding: 18 },

      leftTitle: {
        fontWeight: 950,
        color: "#1f2a44",
        fontSize: 14,
        marginBottom: 12,
      },

      avatarWrap: {
        width: "100%",
        display: "grid",
        placeItems: "center",
        padding: "10px 0 2px",
      },
      avatarCircle: {
        width: 190,
        height: 190,
        borderRadius: 999,
        background: "radial-gradient(circle at 30% 30%, #dbeafe, #e9d5ff)",
        border: "1px solid #e6ebf5",
        overflow: "hidden",
        boxShadow: "0 18px 40px rgba(30, 64, 175, 0.12)",
        display: "grid",
        placeItems: "center",
      },
      avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
      avatarPlaceholder: {
        width: 84,
        height: 84,
        borderRadius: 999,
        background: "rgba(255,255,255,0.9)",
        border: "1px solid rgba(59,130,246,0.25)",
      },

      uploadBtn: {
        marginTop: 14,
        width: "100%",
        padding: "12px 14px",
        borderRadius: 12,
        border: "1px solid rgba(236,72,153,0.25)",
        background:
          "linear-gradient(180deg, rgba(236,72,153,0.12), rgba(236,72,153,0.06))",
        color: "#b91c1c",
        fontWeight: 950,
        cursor: busy ? "not-allowed" : "pointer",
        textAlign: "center",
      },
      uploadHint: {
        marginTop: 10,
        fontSize: 12,
        color: "#64748b",
        lineHeight: 1.4,
      },

      tip: {
        marginTop: 14,
        display: "flex",
        gap: 10,
        alignItems: "center",
        padding: 12,
        borderRadius: 12,
        background: "linear-gradient(180deg, #f7fbff, #ffffff)",
        border: "1px solid #e6ebf5",
        color: "#334155",
        fontSize: 12,
        fontWeight: 800,
      },
      tipDot: {
        width: 14,
        height: 14,
        borderRadius: 999,
        background: "linear-gradient(135deg, #22c55e, #3b82f6)",
      },

      sectionTitle: {
        fontWeight: 950,
        fontSize: 18,
        color: "#1f2a44",
        marginBottom: 14,
      },

      row2: {
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
        gap: 14,
      },
      row3: {
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr 1fr",
        gap: 14,
      },

      field: { display: "grid", gap: 8, marginBottom: 14 },
      label: { fontSize: 12, fontWeight: 950, color: "#334155" },

      control: {
        width: "100%",
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #dbe3f3",
        background: "#ffffff",
        color: "#0f172a",
        outline: "none",
        fontSize: 16,
      },

      select: {
        width: "100%",
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #dbe3f3",
        background: "#ffffff",
        color: "#0f172a",
        outline: "none",
        fontSize: 16,
        appearance: "none",
        WebkitAppearance: "none",
      },

      option: { background: "#ffffff", color: "#0f172a" },

      textarea: {
        width: "100%",
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid #dbe3f3",
        background: "#ffffff",
        color: "#0f172a",
        outline: "none",
        fontSize: 16,
        minHeight: 120,
        resize: "vertical",
      },

      hint: { fontSize: 12, color: "#64748b", lineHeight: 1.35 },

      chipRow: { display: "flex", flexWrap: "wrap", gap: 10 },
      chip: (on) => ({
        padding: "10px 12px",
        borderRadius: 999,
        border: `1px solid ${on ? "rgba(236,72,153,0.30)" : "#dbe3f3"}`,
        background: on ? "rgba(236,72,153,0.10)" : "#ffffff",
        color: "#1f2a44",
        fontWeight: 900,
        cursor: "pointer",
        userSelect: "none",
      }),

      pillRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 10,
      },
      pill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 999,
        background: "#ffffff",
        border: "1px solid #dbe3f3",
        fontSize: 12,
        fontWeight: 900,
        color: "#1f2a44",
      },
      pillX: {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontWeight: 950,
        color: "#ef4444",
      },

      photoGrid: {
        display: "grid",
        gridTemplateColumns: isNarrow ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
        gap: 12,
        marginTop: 12,
      },
      photoCard: {
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid #e6ebf5",
        background: "#ffffff",
      },
      photoImg: {
        width: "100%",
        height: 140,
        objectFit: "cover",
        display: "block",
      },
      photoBar: { display: "flex", gap: 8, padding: 10 },
      photoBtn: {
        flex: 1,
        padding: "10px 10px",
        borderRadius: 12,
        border: "1px solid #dbe3f3",
        background: "#ffffff",
        color: "#1f2a44",
        fontWeight: 950,
        cursor: busy ? "not-allowed" : "pointer",
        fontSize: 12,
      },
      photoBtnPrimary: {
        flex: 1,
        padding: "10px 10px",
        borderRadius: 12,
        border: "1px solid rgba(59,130,246,0.25)",
        background:
          "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(236,72,153,0.08))",
        color: "#1f2a44",
        fontWeight: 980,
        cursor: busy ? "not-allowed" : "pointer",
        fontSize: 12,
      },

      actions: {
        display: "flex",
        gap: 10,
        justifyContent: "space-between",
        marginTop: 10,
        paddingTop: 14,
        borderTop: "1px solid #e6ebf5",
      },
      btn: {
        padding: "12px 14px",
        borderRadius: 12,
        border: "1px solid #dbe3f3",
        background: "#ffffff",
        color: "#1f2a44",
        fontWeight: 950,
        cursor: busy ? "not-allowed" : "pointer",
      },
      primaryBtn: {
        padding: "12px 14px",
        borderRadius: 12,
        border: "none",
        background: "linear-gradient(90deg, #3b82f6, #ec4899)",
        color: "#fff",
        fontWeight: 980,
        cursor: busy ? "not-allowed" : "pointer",
        minWidth: 180,
        boxShadow: "0 12px 26px rgba(59,130,246,0.18)",
      },

      toast: (ok) => ({
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        border: `1px solid ${
          ok ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"
        }`,
        background: ok ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
        color: ok ? "#166534" : "#991b1b",
        fontSize: 13,
        lineHeight: 1.45,
        fontWeight: 800,
      }),

      legal: { marginTop: 12, fontSize: 12, color: "#64748b" },
      legalLink: { color: "#2563eb", fontWeight: 900, cursor: "pointer" },

      // ✅ IN-APP POPUP styles
      popOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(2, 6, 23, 0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 9999,
        padding: 16,
      },
      popCard: {
        width: "100%",
        maxWidth: 420,
        background: "#ffffff",
        border: "1px solid #e6ebf5",
        borderRadius: 16,
        boxShadow: "0 22px 60px rgba(2, 6, 23, 0.35)",
        overflow: "hidden",
      },
      popTop: {
        padding: "14px 16px",
        background:
          "linear-gradient(90deg, rgba(59,130,246,0.12), rgba(236,72,153,0.10))",
        borderBottom: "1px solid #e6ebf5",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      },
      popTitle: { fontWeight: 950, color: "#1f2a44" },
      popX: {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: 18,
        fontWeight: 950,
        color: "#0f172a",
      },
      popBody: { padding: 16, color: "#0f172a" },
      popMsg: {
        fontSize: 14,
        lineHeight: 1.45,
        color: "#334155",
        fontWeight: 800,
      },
      popActions: {
        padding: 16,
        borderTop: "1px solid #e6ebf5",
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
      },
      popOk: {
        padding: "12px 14px",
        borderRadius: 12,
        border: "none",
        background: "linear-gradient(90deg, #3b82f6, #ec4899)",
        color: "#fff",
        fontWeight: 980,
        cursor: "pointer",
        minWidth: 120,
      },
    }),
    [busy, isNarrow]
  );

  /** ---------- load user + prefill ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u?.uid) {
        nav("/login", {
          replace: true,
          state: { msg: "Please login to continue." },
        });
        return;
      }

      setEmail(u.email || "");

      try {
        const refDoc = doc(db, "datingProfiles", u.uid);
        const snap = await getDoc(refDoc);

        if (snap.exists()) {
          const data = snap.data() || {};
          if (data.profileComplete) {
            nav("/profile", { replace: true });
            return;
          }

          setDisplayName(safe(data.displayName) || safe(u.displayName));
          setUsername(safe(data.username));
          setGender(safe(data.gender) || "female");
          setLookingFor(safe(data.lookingFor) || "male");

          const bd = data.birthDate?.toDate?.() ? data.birthDate.toDate() : null;
          if (bd) setBirthDate(new Date(bd).toISOString().slice(0, 10));
          else setBirthDate(safe(data.birthDate));

          setCity(safe(data.city));
          setRegion(safe(data.region));
          setCountry(safe(data.country) || "Ghana");
          setBio(safe(data.bio));

          setHeight(data.height != null ? String(data.height) : "");
          setEducation(safe(data.education) || "none");
          setJob(safe(data.job) || "none");
          setJobOther(safe(data.jobOther));

          // ✅ relationship goal load
          setRelationshipGoal(safe(data.relationshipGoal) || "");

          setSmoking(safe(data.smoking) || "no");
          setDrinking(safe(data.drinking) || "occasionally");
          setReligion(safe(data.religion) || "none");

          // ✅ preferred age load (min + max)
          setPrefAgeMin(
            data?.prefAgeMin != null ? String(data.prefAgeMin) : "18"
          );
          setPrefAgeMax(
            data?.prefAgeMax != null ? String(data.prefAgeMax) : ""
          );

          setLanguages(
            Array.isArray(data.languages) ? uniq(data.languages) : ["English"]
          );
          setInterests(Array.isArray(data.interests) ? uniq(data.interests) : []);

          setPhotos(Array.isArray(data.photos) ? data.photos.filter(Boolean) : []);
          setAvatar(safe(data.avatar));
        } else {
          // ✅ auto-create doc if missing
          await setDoc(
            refDoc,
            {
              uid: u.uid,
              email: u.email || "",
              profileComplete: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              prefAgeMin: 18,
              prefAgeMax: null,
            },
            { merge: true }
          );

          setDisplayName(safe(u.displayName));
          setCountry("Ghana");
          setLanguages(["English"]);
          setRelationshipGoal("");
          setPrefAgeMin("18");
          setPrefAgeMax("");
        }
      } catch (e) {
        console.error("[DATING_PROFILE] load error:", e);
      }
    });

    return () => unsub();
  }, [nav]);

  /** ---------- firestore utils ---------- */
  const isUsernameTaken = async (uname, myUid) => {
    const u = slugifyUsername(uname);
    if (!u) return true;

    const qy = query(
      collection(db, "datingProfiles"),
      where("username", "==", u),
      limit(1)
    );
    const snap = await getDocs(qy);
    if (snap.empty) return false;

    const matchId = snap.docs[0].id;
    return matchId !== myUid;
  };

  const uploadOnePhoto = async (file) => {
    const u = auth.currentUser;
    if (!u?.uid) throw new Error("Not logged in");
    if (!file) throw new Error("No file selected");

    const maxMB = 6;
    if (file.size > maxMB * 1024 * 1024)
      throw new Error(`Image too large. Max ${maxMB}MB`);

    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const path = `datingProfiles/${u.uid}/photos/${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}.${ext}`;

    const r = ref(storage, path);
    await uploadBytes(r, file, { contentType: file.type || "image/jpeg" });
    return await getDownloadURL(r);
  };

  const onPickPhotos = async (files) => {
    try {
      setMsg("");
      setBusy(true);

      const list = Array.from(files || []);
      if (!list.length) return;

      const sliced = list.slice(0, 6);

      const uploaded = [];
      for (const f of sliced) {
        const url = await uploadOnePhoto(f);
        uploaded.push(url);
      }

      const nextPhotos = uniq([...(photos || []), ...uploaded]).slice(0, 9);
      setPhotos(nextPhotos);
      if (!avatar && nextPhotos[0]) setAvatar(nextPhotos[0]);
    } catch (e) {
      console.error("[DATING_PROFILE] upload error:", e);
      setMsg(e?.message || "Failed to upload photo");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = (url) => {
    const next = (photos || []).filter((p) => p !== url);
    setPhotos(next);
    if (avatar === url) setAvatar(next[0] || "");
  };

  const toggleInterest = (name) => {
    const item = safe(name).toLowerCase();
    if (!item) return;
    setInterests((prev) => {
      const has = (prev || []).includes(item);
      const next = has
        ? (prev || []).filter((x) => x !== item)
        : uniq([...(prev || []), item]);
      return next.slice(0, 25);
    });
  };

  const addCustomInterest = (v) => {
    const item = safe(v).toLowerCase();
    if (!item) return;
    setInterests((prev) => uniq([...(prev || []), item]).slice(0, 25));
  };

  const addLanguage = (v) => {
    const item = safe(v);
    if (!item) return;
    setLanguages((prev) => uniq([...(prev || []), item]).slice(0, 10));
  };

  /** ---------- validation per step ---------- */
  const stepError = useMemo(() => {
    if (step === 0) {
      if (!safe(displayName)) return "Please enter your display name.";
      if (!safe(username)) return "Please choose a username.";
      if (!safe(gender)) return "Select your gender.";
      if (!safe(lookingFor)) return "Select who you're looking for.";
      if (!safe(birthDate)) return "Add your birth date.";
      const a = calcAgeFromISODate(birthDate);
      if (a == null) return "Birth date is invalid.";
      return "";
    }
    if (step === 1) {
      if (!safe(city)) return "City is required.";
      return "";
    }
    if (step === 2) {
      if (safe(bio).length > 500)
        return "Bio is too long. Keep it under 500 characters.";
      return "";
    }
    return "";
  }, [step, displayName, username, gender, lookingFor, birthDate, city, bio]);

  const goNext = async () => {
    setMsg("");

    if (stepError) {
      setMsg(stepError);
      return;
    }

    if (step === 0) {
      const u = auth.currentUser;
      const uname = slugifyUsername(username);
      if (!uname) {
        setMsg("Username is required (letters/numbers/underscore only).");
        return;
      }
      try {
        setBusy(true);
        const taken = await isUsernameTaken(uname, u?.uid);
        if (taken) {
          setMsg("That username is already taken. Try another one.");
          return;
        }
        if (uname !== username) setUsername(uname);
      } catch (e) {
        setMsg(e?.message || "Could not check username.");
        return;
      } finally {
        setBusy(false);
      }
    }

    setStep((s) => Math.min(4, s + 1));
  };

  /** ---------- final save ---------- */
  const onSave = async () => {
    try {
      setMsg("");
      setBusy(true);

      const u = auth.currentUser;
      if (!u?.uid) throw new Error("Not logged in");

      const uname = slugifyUsername(username);
      if (!uname) throw new Error("Username is required.");

      const taken = await isUsernameTaken(uname, u.uid);
      if (taken) throw new Error("That username is already taken.");

      const computedAge = calcAgeFromISODate(birthDate);
      if (computedAge == null) throw new Error("Birth date is invalid.");

      if (!safe(city)) throw new Error("City is required.");

      // ✅ NEW: IN-APP POPUP: profile picture (at least 1) required at save
      if (!(photos || []).length) {
        setStep(3); // go to Photo Upload step
        setBusy(false);
        showPop("Please upload at least 1 profile picture to continue.");
        return;
      }

      // ✅ also ensure an avatar is set (optional but nice)
      if (!safe(avatar)) {
        const first = (photos || [])[0] || "";
        if (first) setAvatar(first);
      }

      // ✅ IN-APP POPUP: relationship goal required at save
      if (!safe(relationshipGoal)) {
        setStep(4);
        setBusy(false);
        showPop("Please choose a relationship goal.");
        return;
      }

      // ✅ IN-APP POPUP: preferred age max required at save
      if (!safe(prefAgeMax)) {
        setStep(4);
        setBusy(false);
        showPop("Please choose the maximum age you want to see.");
        return;
      }

      // ✅ NEW: preferred age min required + valid
      if (!safe(prefAgeMin)) {
        setStep(4);
        setBusy(false);
        showPop("Please choose the minimum age you want to see.");
        return;
      }

      const minAgeNum = Number(prefAgeMin);
      const maxAgeNum = Number(prefAgeMax);

      if (!Number.isFinite(minAgeNum) || minAgeNum < 18) {
        setStep(4);
        setBusy(false);
        showPop("Please choose a valid minimum age (18 or higher).");
        return;
      }

      if (!Number.isFinite(maxAgeNum) || maxAgeNum < 18) {
        setStep(4);
        setBusy(false);
        showPop("Please choose a valid maximum age (18 or higher).");
        return;
      }

      if (minAgeNum > maxAgeNum) {
        setStep(4);
        setBusy(false);
        showPop("Minimum age cannot be higher than maximum age.");
        return;
      }

      const finalJob = job === "other" ? safe(jobOther) : job;

      const profileRef = doc(db, "datingProfiles", u.uid);
      const existing = await getDoc(profileRef);

      await setDoc(
        profileRef,
        {
          uid: u.uid,
          email: u.email || "",

          displayName: safe(displayName),
          username: uname,

          gender: safe(gender),
          lookingFor: safe(lookingFor),

          birthDate: birthDate,
          age: computedAge,

          city: safe(city),
          region: safe(region),
          country: safe(country) || "Ghana",

          bio: safe(bio).slice(0, 500),

          interests: uniq(interests),
          languages: uniq(languages),

          photos: uniq(photos),
          avatar: safe(avatar) || safe(photos?.[0]),

          height: height ? Number(height) : null,
          education: safe(education),
          job: safe(finalJob),
          jobOther: job === "other" ? safe(jobOther) : "",

          relationshipGoal: safe(relationshipGoal),
          smoking: safe(smoking),
          drinking: safe(drinking),
          religion: safe(religion),

          // ✅ dating filter preferences
          prefAgeMin: minAgeNum,
          prefAgeMax: maxAgeNum,

          verified: Boolean(existing.data()?.verified) || false,
          isHidden: Boolean(existing.data()?.isHidden) || false,
          isBanned: Boolean(existing.data()?.isBanned) || false,

          profileComplete: true,
          lastActiveAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdAt: existing.exists()
            ? existing.data()?.createdAt || serverTimestamp()
            : serverTimestamp(),
        },
        { merge: true }
      );

      nav("/profile", { replace: true });
    } catch (e) {
      console.error("[DATING_PROFILE] save error:", e);
      setMsg(e?.message || "Failed to save profile");
    } finally {
      setBusy(false);
    }
  };

  /** ---------- photos drag/drop ---------- */
  const [isOver, setIsOver] = useState(false);

  const onDropFiles = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    if (busy) return;
    const files = e.dataTransfer?.files;
    if (files?.length) await onPickPhotos(files);
  };

  const UploadInput = () => (
    <label style={{ display: "block" }}>
      <input
        style={{ display: "none" }}
        type="file"
        accept="image/*"
        multiple
        disabled={busy}
        onChange={(e) => onPickPhotos(e.target.files)}
      />
      <div style={styles.uploadBtn}>{busy ? "Uploading..." : "Upload Photo"}</div>
    </label>
  );

  const jumpToTab = (tabIndex) => {
    if (busy) return;
    if (tabIndex === 0) setStep(0);
    if (tabIndex === 1) setStep(1);
    if (tabIndex === 2) setStep(4);
    if (tabIndex === 3) setStep(4);
    if (tabIndex === 4) setStep(3);
  };

  return (
    <div style={styles.page}>
      {/* ✅ IN-APP POPUP MODAL */}
      {popOpen ? (
        <div
          style={styles.popOverlay}
          role="dialog"
          aria-modal="true"
          onClick={closePop}
        >
          <div style={styles.popCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.popTop}>
              <div style={styles.popTitle}>Fix required</div>
              <button type="button" style={styles.popX} onClick={closePop}>
                ✕
              </button>
            </div>
            <div style={styles.popBody}>
              <div style={styles.popMsg}>{popText}</div>
            </div>
            <div style={styles.popActions}>
              <button type="button" style={styles.popOk} onClick={closePop}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ CLEAN Top Nav */}
      <div style={styles.nav}>
        <div style={styles.navInner}>
          <div
            style={styles.brand}
            onClick={() => nav("/profile")}
            title="Go to profile"
            role="button"
            tabIndex={0}
          >
            <span style={styles.brandDot} />
            <span style={styles.brandText}>GhanaDating</span>
          </div>

          <div style={styles.navRight}>
            <div style={styles.navPill} title={email || ""}>
              Signed in: {email || "—"}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.wrap}>
        <div style={styles.title}>Create Your Profile</div>
        <div style={styles.subtitle}>
          Tell us about yourself and start matching with great people! Completed
          profiles get <b>5x</b> more attention!
        </div>

        <div style={styles.stepTabs}>
          {tabs.map((t, i) => (
            <div
              key={t.key}
              style={styles.tab(i === activeTabIndex)}
              onClick={() => jumpToTab(i)}
              role="button"
              tabIndex={0}
            >
              <div style={styles.tabNum(i === activeTabIndex)}>{i + 1}</div>
              <div>{t.label}</div>
            </div>
          ))}
        </div>

        <div style={styles.grid}>
          {/* Left Photo Card */}
          <div style={{ ...styles.card, ...styles.leftCard }}>
            <div style={styles.leftTitle}>Profile Photo</div>

            <div style={styles.avatarWrap}>
              <div style={styles.avatarCircle}>
                {avatar ? (
                  <img src={avatar} alt="avatar" style={styles.avatarImg} />
                ) : (
                  <div style={styles.avatarPlaceholder} />
                )}
              </div>
            </div>

            <UploadInput />

            <div style={styles.uploadHint}>JPG, GIF, or PNG. Max size: 6MB.</div>

            <div style={styles.tip}>
              <span style={styles.tipDot} />
              Profiles with photos get up to <b>10x</b> more matches!
            </div>

            {photos?.length ? (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                  Your photos ({photos.length}/9)
                </div>

                <div style={styles.photoGrid}>
                  {photos.slice(0, 6).map((url) => (
                    <div key={url} style={styles.photoCard}>
                      <img src={url} alt="uploaded" style={styles.photoImg} />
                      <div style={styles.photoBar}>
                        <button
                          style={
                            avatar === url ? styles.photoBtnPrimary : styles.photoBtn
                          }
                          disabled={busy}
                          onClick={() => setAvatar(url)}
                          type="button"
                        >
                          {avatar === url ? "Avatar ✓" : "Set"}
                        </button>
                        <button
                          style={styles.photoBtn}
                          disabled={busy}
                          onClick={() => removePhoto(url)}
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Right Form Card */}
          <div style={{ ...styles.card, ...styles.rightCard }}>
            <div style={styles.sectionTitle}>
              {step === 0 && "Basic Information"}
              {step === 1 && "Location"}
              {step === 2 && "About Me"}
              {step === 3 && "Photo Upload"}
              {step === 4 && "Interests & Lifestyle"}
            </div>

            {step === 0 && (
              <>
                <div style={styles.row2}>
                  <Field styles={styles} label="Name *">
                    <input
                      style={styles.control}
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Enter your name"
                      autoComplete="name"
                    />
                  </Field>

                  <Field styles={styles} label="Gender *">
                    <select
                      style={styles.select}
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <option style={styles.option} value="female">
                        Female
                      </option>
                      <option style={styles.option} value="male">
                        Male
                      </option>
                      <option style={styles.option} value="other">
                        Other
                      </option>
                    </select>
                  </Field>
                </div>

                <div style={styles.row3}>
                  <Field styles={styles} label="Username *">
                    <input
                      style={styles.control}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter a username"
                      autoComplete="username"
                    />
                    <div style={styles.hint}>
                      Letters/numbers/underscore only. Auto-fix on Next.
                    </div>
                  </Field>

                  <Field styles={styles} label="Looking for *">
                    <select
                      style={styles.select}
                      value={lookingFor}
                      onChange={(e) => setLookingFor(e.target.value)}
                    >
                      <option style={styles.option} value="male">
                        Male
                      </option>
                      <option style={styles.option} value="female">
                        Female
                      </option>
                      <option style={styles.option} value="everyone">
                        Everyone
                      </option>
                    </select>
                  </Field>

                  <Field styles={styles} label="Date of Birth *">
                    <input
                      style={styles.control}
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                    />
                    <div style={styles.hint}>
                      {age != null ? `Age: ${age}` : "We calculate your age from this."}
                    </div>
                  </Field>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div style={styles.row2}>
                  <Field styles={styles} label="Location (City) *">
                    <input
                      style={styles.control}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Enter your location"
                      autoComplete="address-level2"
                    />
                  </Field>

                  <Field styles={styles} label="Region">
                    <input
                      style={styles.control}
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      placeholder="Greater Accra"
                      autoComplete="address-level1"
                    />
                  </Field>
                </div>

                <Field styles={styles} label="Country">
                  <input
                    style={styles.control}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Ghana"
                    autoComplete="country-name"
                  />
                </Field>
              </>
            )}

            {step === 2 && (
              <Field styles={styles} label="About Me">
                <textarea
                  style={styles.textarea}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Write a short description about yourself and the type of relationship you're looking for."
                />
                <div style={styles.hint}>0 / 500 (we’ll limit it for you)</div>
              </Field>
            )}

            {step === 3 && (
              <>
                <div
                  style={{
                    borderRadius: 14,
                    border: `1px dashed ${
                      isOver ? "rgba(236,72,153,0.60)" : "#dbe3f3"
                    }`,
                    background: isOver ? "rgba(236,72,153,0.06)" : "#ffffff",
                    padding: 14,
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOver(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOver(false);
                  }}
                  onDrop={onDropFiles}
                >
                  <div style={{ fontWeight: 950, color: "#1f2a44" }}>
                    Drag & drop your photos here
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                    Or click upload in the left panel. Up to 9 photos.
                  </div>
                </div>

                {photos?.length ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                      Manage photos ({photos.length}/9)
                    </div>

                    <div style={styles.photoGrid}>
                      {photos.map((url) => (
                        <div key={url} style={styles.photoCard}>
                          <img src={url} alt="uploaded" style={styles.photoImg} />
                          <div style={styles.photoBar}>
                            <button
                              style={
                                avatar === url ? styles.photoBtnPrimary : styles.photoBtn
                              }
                              disabled={busy}
                              onClick={() => setAvatar(url)}
                              type="button"
                            >
                              {avatar === url ? "Avatar ✓" : "Set avatar"}
                            </button>
                            <button
                              style={styles.photoBtn}
                              disabled={busy}
                              onClick={() => removePhoto(url)}
                              type="button"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 12, ...styles.hint }}>
                    No photos yet. Add 1–3 clear selfies to look real.
                  </div>
                )}
              </>
            )}

            {step === 4 && (
              <>
                <Field styles={styles} label="Interests (pick a few)">
                  <div style={styles.chipRow}>
                    {interestOptions.map((x) => {
                      const key = x.toLowerCase();
                      const on = (interests || []).includes(key);
                      return (
                        <div
                          key={key}
                          style={styles.chip(on)}
                          onClick={() => toggleInterest(key)}
                        >
                          {titleCase(x)}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <CustomAdd
                      styles={styles}
                      busy={busy}
                      placeholder="Add custom interest (e.g. swimming)"
                      onAdd={addCustomInterest}
                      btnText="Add"
                    />
                  </div>
                </Field>

                <Field styles={styles} label="Languages">
                  <div style={styles.row2}>
                    <select
                      style={styles.select}
                      value=""
                      disabled={busy}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) addLanguage(v === "Other" ? "Other" : v);
                      }}
                    >
                      <option style={styles.option} value="">
                        Choose a language…
                      </option>
                      {languageOptions.map((x) => (
                        <option style={styles.option} key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </select>

                    <CustomAdd
                      styles={styles}
                      busy={busy}
                      placeholder="Or type language (e.g. Dagbani)"
                      onAdd={addLanguage}
                      btnText="Add"
                    />
                  </div>

                  {languages?.length ? (
                    <div style={styles.pillRow}>
                      {languages.map((x) => (
                        <span key={x} style={styles.pill}>
                          {x}
                          <button
                            type="button"
                            style={styles.pillX}
                            onClick={() =>
                              setLanguages((prev) => (prev || []).filter((i) => i !== x))
                            }
                            disabled={busy}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </Field>

                <div style={styles.row2}>
                  <Field styles={styles} label="Drinking">
                    <select
                      style={styles.select}
                      value={drinking}
                      onChange={(e) => setDrinking(e.target.value)}
                    >
                      {drinkingOptions.map((x) => (
                        <option style={styles.option} key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field styles={styles} label="Smoking">
                    <select
                      style={styles.select}
                      value={smoking}
                      onChange={(e) => setSmoking(e.target.value)}
                    >
                      {smokingOptions.map((x) => (
                        <option style={styles.option} key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                {/* ✅ Preferred age range */}
                <div style={styles.row2}>
                  <Field styles={styles} label="I want to see ages from *">
                    <select
                      style={styles.select}
                      value={prefAgeMin}
                      onChange={(e) => setPrefAgeMin(e.target.value)}
                    >
                      {ageOptions.map((n) => (
                        <option style={styles.option} key={n} value={String(n)}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <div style={styles.hint}>Choose your minimum age.</div>
                  </Field>

                  <Field styles={styles} label="Up to (max age) *">
                    <select
                      style={styles.select}
                      value={prefAgeMax}
                      onChange={(e) => setPrefAgeMax(e.target.value)}
                    >
                      <option style={styles.option} value="">
                        — Choose max age —
                      </option>
                      {ageOptions.map((n) => (
                        <option style={styles.option} key={n} value={String(n)}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <div style={styles.hint}>Required.</div>
                  </Field>
                </div>

                <div style={styles.row2}>
                  <Field styles={styles} label="Relationship goal *">
                    <select
                      style={styles.select}
                      value={relationshipGoal}
                      onChange={(e) => setRelationshipGoal(e.target.value)}
                    >
                      {relationshipGoalOptions.map((x) => (
                        <option style={styles.option} key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                    <div style={styles.hint}>Required.</div>
                  </Field>

                  <Field styles={styles} label="Education">
                    <select
                      style={styles.select}
                      value={education}
                      onChange={(e) => setEducation(e.target.value)}
                    >
                      {educationOptions.map((x) => (
                        <option style={styles.option} key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div style={styles.row2}>
                  <Field styles={styles} label="Job">
                    <select
                      style={styles.select}
                      value={job}
                      onChange={(e) => setJob(e.target.value)}
                    >
                      {jobOptions.map((x) => (
                        <option style={styles.option} key={x.value} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>

                    {job === "other" ? (
                      <div style={{ marginTop: 10 }}>
                        <input
                          style={styles.control}
                          value={jobOther}
                          onChange={(e) => setJobOther(e.target.value)}
                          placeholder="Type your job…"
                        />
                        <div style={styles.hint}>
                          Example: “Designer”, “Mechanic”, “Farmer”.
                        </div>
                      </div>
                    ) : null}
                  </Field>

                  <Field styles={styles} label="Height (cm)">
                    <input
                      style={styles.control}
                      value={height}
                      onChange={(e) =>
                        setHeight(e.target.value.replace(/[^\d]/g, "").slice(0, 3))
                      }
                      placeholder="e.g. 170"
                      inputMode="numeric"
                    />
                    <div style={styles.hint}>Optional.</div>
                  </Field>
                </div>

                <Field styles={styles} label="Religion">
                  <select
                    style={styles.select}
                    value={religion}
                    onChange={(e) => setReligion(e.target.value)}
                  >
                    {religionOptions.map((x) => (
                      <option style={styles.option} key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            <div style={styles.actions}>
              <button
                style={styles.btn}
                disabled={busy || step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                type="button"
              >
                Back
              </button>

              {step < 4 ? (
                <button
                  style={styles.primaryBtn}
                  disabled={busy}
                  onClick={goNext}
                  type="button"
                >
                  {busy ? "Checking..." : "Continue"}
                </button>
              ) : (
                <button
                  style={styles.primaryBtn}
                  disabled={busy}
                  onClick={onSave}
                  type="button"
                >
                  {busy ? "Saving..." : "Finish & Save"}
                </button>
              )}
            </div>

            <div style={styles.legal}>
              By continuing, you agree to our{" "}
              <span style={styles.legalLink} onClick={() => nav("/terms")}>
                Terms of Service
              </span>{" "}
              and{" "}
              <span style={styles.legalLink} onClick={() => nav("/privacy")}>
                Privacy Policy
              </span>
              .
            </div>

            {msg ? (
              <div style={styles.toast(msg.toLowerCase().includes("success"))}>{msg}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ---------------- UI helpers ---------------- */
function Field({ styles, label, children }) {
  return (
    <div style={styles.field}>
      <div style={styles.label}>{label}</div>
      {children}
    </div>
  );
}

function CustomAdd({ styles, busy, placeholder, onAdd, btnText }) {
  const [val, setVal] = useState("");

  return (
    <div style={{ display: "flex", gap: 10 }}>
      <input
        style={styles.control}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        disabled={busy}
      />
      <button
        style={{
          padding: "12px 14px",
          borderRadius: 12,
          border: "none",
          background: "linear-gradient(90deg, #3b82f6, #ec4899)",
          color: "#fff",
          fontWeight: 980,
          cursor: busy ? "not-allowed" : "pointer",
          minWidth: 110,
        }}
        type="button"
        disabled={busy}
        onClick={() => {
          onAdd(val);
          setVal("");
        }}
      >
        {btnText}
      </button>
    </div>
  );
}